import { ArticleModel } from './models/ArticleModel';
import {
    fetchArticleById,
    fetchArticlesByAuthorId,
    fetchArticlesByCategoryId,
    fetchArticlesByTagId,
    fetchArticlesByText,
    fetchImportantArticles,
    fetchLatestArticlesProxy,
} from './http/ArticleHttp';
import {
    normalizeArticle,
    normalizeArticles,
    normalizePartialArticle,
    normalizePartialArticles,
} from './normalizers/ArticleNormalizer';
import { ListModel } from '../list/models/ListModel';
import { getListBySlug } from '../list/List';
import { getTagIdBySlug } from '../tag/Tag';
import { normalizeMetadata } from '../meta/normalizers/MetaDataNormalizer';
import { ArticleListingModel } from './models/ArticleListingModel';
import { ArticlePartialModel } from './models/ArticlePartialModel';

export const getLatestNumberOfArticles = async (numberOfArticles: number): Promise<ArticleModel[]> => {
    try {
        const response: any = await fetchLatestArticlesProxy(`${numberOfArticles}`);
        return normalizeArticles(response.content);
    } catch (e) {
        console.error('There was a problem retrieving the latest articles: ', e);
        return [];
    }
};

export const getArticleById = async (articleId: string): Promise<ArticleModel> => {
    const response = await fetchArticleById(articleId);
    return normalizeArticle(response);
};

export const getLeadingContentOfListBySlug = async (
    slug: string,
    numberOfContent: number = 3,
): Promise<ArticlePartialModel[]> => {
    const articles: ArticlePartialModel[] = await getArticlesByListSlug(slug);
    return articles.filter((content, idx) => idx < numberOfContent);
};

export const getArticlesByListSlug = async (slug: string): Promise<ArticlePartialModel[]> => {
    const list: ListModel | null = await getListBySlug(slug);
    if (list && list.items) {
        return list.items as ArticlePartialModel[];
    }
    return [];
};

export const getArticlesByCategoryId = async (
    id: string = '',
    limit?: string,
    page?: string,
    offset?: string,
): Promise<ArticleListingModel | null> => {
    try {
        if (id) {
            const articlesResponse = await fetchArticlesByCategoryId(id, limit, page, offset);
            const articles = normalizePartialArticles(articlesResponse.data);
            const meta = normalizeMetadata(articlesResponse.meta);
            return { articles, meta };
        }

        return null;
    } catch (e) {
        console.error('Error getting articles by category id', e);
        return null;
    }
};

export const searchArticles = async (
    query: string,
    limit?: string,
    page?: string,
): Promise<ArticleListingModel | null> => {
    try {
        const articlesResponse = await fetchArticlesByText(query, limit, page);
        const articles = normalizeArticles(articlesResponse.data);
        const meta = normalizeMetadata(articlesResponse.meta);
        return { articles, meta };
    } catch (e) {
        console.error('Error retrieving articles by tag id: ', e);
        return null;
    }
};

export const getArticlesByTagSlug = async (
    slug: string,
    limit?: string,
    page?: string,
): Promise<ArticleListingModel | null> => {
    try {
        const tagId = await getTagIdBySlug(slug);
        const articlesResponse = await fetchArticlesByTagId(tagId, limit, page);
        const articles = normalizeArticles(articlesResponse.data);
        const meta = normalizeMetadata(articlesResponse.meta);
        return { articles, meta };
    } catch (e) {
        console.error('Error retrieving articles by tag id: ', e);
        return null;
    }
};

export const articleFooterToArray = (article: ArticleModel): string[] => {
    if (article.footer && article.footer.length > 0) {
        return article.footer.split('|').map((item: string) => item.trim());
    }

    return [];
};

export const getArticlesByAuthorId = async (
    id: string,
    limit?: string,
    page?: string,
): Promise<ArticleListingModel | null> => {
    try {
        const response = await fetchArticlesByAuthorId(id, limit, page);
        const articles = normalizeArticles(response.data);
        const meta = normalizeMetadata(response.meta);
        return { articles, meta };
    } catch (e) {
        console.error('Error retrieving articles by author id: ', e);
        return null;
    }
};

export const removeRepeatingArticles = (articles: ArticlePartialModel[], toRemoveArticles: ArticlePartialModel[]) => {
    const articlesToBeRemovedIds = toRemoveArticles.map((article) => article.id);
    return articles.filter((article) => !articlesToBeRemovedIds.includes(article.id));
};

export const getImportantArticles = async (limit: string): Promise<ArticlePartialModel[] | null> => {
    try {
        const response = await fetchImportantArticles(limit);
        return response.data.map((article: any) => {
            return normalizePartialArticle(article);
        });
    } catch (e) {
        console.error('Error retrieving important articles', e);
        return null;
    }
};
